import { CookieCategory, CookieConsentPreferences } from '@/types/consent';

/**
 * Get all cookies as an object
 */
export const getCookies = (): Record<string, string> => {
  const cookies: Record<string, string> = {};
  document.cookie.split(';').forEach((cookie) => {
    const [name, value] = cookie.trim().split('=');
    if (name) {
      cookies[name] = decodeURIComponent(value || '');
    }
  });
  return cookies;
};

/**
 * Get a specific cookie by name
 */
export const getCookie = (name: string): string | null => {
  const cookies = getCookies();
  return cookies[name] || null;
};

/**
 * Set a cookie
 */
export const setCookie = (
  name: string,
  value: string,
  days?: number,
  path: string = '/'
): void => {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${encodeURIComponent(value || '')}${expires}; path=${path}; SameSite=Lax`;
};

/**
 * Delete a cookie
 */
export const deleteCookie = (name: string, path: string = '/'): void => {
  document.cookie = `${name}=; Max-Age=-99999999; path=${path}`;
};

/**
 * Check if a cookie category is allowed based on preferences
 */
export const isCategoryAllowed = (
  category: CookieCategory,
  preferences: CookieConsentPreferences
): boolean => {
  if (category === 'necessary') return true;
  return preferences[category] === true;
};

/**
 * Set a cookie only if the category is allowed
 */
export const setCookieWithConsent = (
  name: string,
  value: string,
  category: CookieCategory,
  preferences: CookieConsentPreferences,
  days?: number
): boolean => {
  if (isCategoryAllowed(category, preferences)) {
    setCookie(name, value, days);
    return true;
  }
  return false;
};

/**
 * Clear cookies for a specific category
 * Note: This requires a mapping of cookies to categories, which is imperfect
 * as we can't know all cookies set by third-party scripts.
 */
export const clearCookiesByCategory = (
  category: CookieCategory,
  cookiesToDelete: string[]
): void => {
  if (category === 'necessary') return; // Never clear necessary cookies

  cookiesToDelete.forEach((cookieName) => {
    deleteCookie(cookieName);
  });
};
